import { getLimit, isFeatureOn } from './appConfig';
import { isStoreBuild } from './platform';
import { fsAvailable } from './recordStore';

/**
 * «Запись без связи» — выключатели и отметки (решения владельца 02.10.2026).
 *
 * Три слоя, и у каждого свой рубильник:
 *
 * | слой | что | кому | чем гасится |
 * |---|---|---|---|
 * | А | честные состояния, запись не теряется молча | всем | ничем — это починка |
 * | Б | `client_id`, опоздавшая запись, журнал доставки | всем, сайт и приложение | `features.record_client_id` |
 * | В | запись файлом на телефоне | только приложение | `features.offline_records` И тумблер ученика |
 *
 * 🔴 ОБА ФЛАГА В СБОРКЕ ВЫКЛЮЧЕНЫ. Обновлений мимо магазина нет: ошибка в новом слое жила бы до следующей
 * сборки и её проверки. Поэтому всё новое включается с сервера (`PUT /admin/app-config`) и так же гасится;
 * молчащий сервер, пустая таблица, старая сборка — всё это «выключено» (`isFeatureOn`).
 *
 * ⚠️ ФЛАГИ ЧИТАЮТСЯ В МОМЕНТ ДЕЙСТВИЯ, НЕ ЗАПОМИНАЮТСЯ В ЗАПИСИ. Выключили с сервера — уже лежащая в очереди
 * запись уйдёт без `client_id`; файл, записанный при включённом слое В, дочитается и уйдёт в любом случае.
 *
 * ⚠️ ВЫБОР УЧЕНИКА — НА УСТРОЙСТВЕ, как вибрация и напоминания: читать его надо синхронно и именно тогда,
 * когда связи нет. Новый телефон или переустановка — снова «выключено».
 */

const TOGGLE_KEY = 'onebilim:offlineRecords';
const SEEN_KEY = 'onebilim:voiceTaskSeen';
const HINT_KEY = 'onebilim:offlineRecordsHint';
const NOTICE_KEY = 'onebilim:recordNotices';

function readRaw(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* хранилище недоступно — выбор не запомнится, запись идёт прежним путём */
  }
}

function readJson<T>(key: string, fallback: T): T {
  const raw = readRaw(key);
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/** Слой Б: слать ли `client_id`, `age_ms` и известия о записи. Сайт и приложение. */
export function recordClientIdOn(): boolean {
  return isFeatureOn('record_client_id');
}

/** Серверный выключатель слоя В. Выключен — тумблера нет вовсе. */
export function offlineRecordsAllowed(): boolean {
  return isFeatureOn('offline_records');
}

/** Что выбрал ученик на этом устройстве. По умолчанию — выключено. */
export function offlineRecordsChosen(): boolean {
  return readRaw(TOGGLE_KEY) === '1';
}

const toggleListeners = new Set<() => void>();

export function subscribeOfflineRecords(l: () => void): () => void {
  toggleListeners.add(l);
  return () => toggleListeners.delete(l);
}

export function setOfflineRecordsChosen(on: boolean): void {
  writeRaw(TOGGLE_KEY, on ? '1' : '0');
  toggleListeners.forEach((l) => l());
}

/**
 * На этом устройстве открывали задание с записью голоса.
 *
 * ⚠️ ЗАЧЕМ ОТМЕТКА. Тумблер, который ничего не меняет, — обещание впустую: ученику без заданий с записью
 * (бесплатный путь, проверяющий из магазина) он не показывается. Гейта по тарифу тут нет намеренно —
 * содержимое открытых курсов меняет методист, а отметка говорит ровно о том, что нужно: запись ему встречалась.
 */
export function voiceTaskSeen(): boolean {
  return readRaw(SEEN_KEY) === '1';
}

export function markVoiceTaskSeen(): void {
  if (!voiceTaskSeen()) writeRaw(SEEN_KEY, '1');
}

/** Показывать ли тумблер в настройках: приложение, сервер разрешил, плагин стоит, запись встречалась. */
export function offlineToggleVisible(): boolean {
  return isStoreBuild() && offlineRecordsAllowed() && fsAvailable() && voiceTaskSeen();
}

/** Слой В работает: оба «включено», и есть куда писать файл. */
export function strongStorageOn(): boolean {
  return isStoreBuild() && offlineRecordsAllowed() && offlineRecordsChosen() && fsAvailable();
}

/** Сколько заданий помним в отметках — чтобы список в хранилище не рос вечно. */
const KEEP = 200;

/**
 * Подсказка «Плохая связь? Включите …» — не чаще раза за задание (решение владельца 02.10.2026).
 * Только там, где тумблер есть и выключен.
 */
export function shouldHintOfflineRecords(blockId: string): boolean {
  if (!isStoreBuild() || !offlineRecordsAllowed() || !fsAvailable() || offlineRecordsChosen()) return false;
  return !readJson<string[]>(HINT_KEY, []).includes(blockId);
}

export function markOfflineHintShown(blockId: string): void {
  const list = readJson<string[]>(HINT_KEY, []).filter((id) => id !== blockId);
  list.push(blockId);
  writeRaw(HINT_KEY, JSON.stringify(list.slice(-KEEP)));
}

/**
 * Заметка ученику: запись снята с очереди отказом сервера, пока задания не было на экране.
 *
 * 🔴 БЕЗ НЕЁ ОТКАЗ В ФОНОВОМ ПРОХОДЕ БЫЛ БЫ НЕМЫМ. Запись лежала неделю без связи, связь появилась, сервер
 * ответил «работу уже приняли» — очередь запись сняла. Ученик открывает задание: записи нет, объяснения нет.
 */
type Notice = { blockId: string; code: string; at: number };

export function addRecordNotice(blockId: string, code: string): void {
  const list = readJson<Notice[]>(NOTICE_KEY, []).filter((n) => n.blockId !== blockId);
  list.push({ blockId, code, at: Date.now() });
  writeRaw(NOTICE_KEY, JSON.stringify(list.slice(-KEEP)));
}

/** Забрать заметку: показывается у задания один раз. */
export function takeRecordNotice(blockId: string): string | undefined {
  const list = readJson<Notice[]>(NOTICE_KEY, []);
  const found = list.find((n) => n.blockId === blockId);
  if (!found) return undefined;
  writeRaw(NOTICE_KEY, JSON.stringify(list.filter((n) => n.blockId !== blockId)));
  return found.code;
}

/**
 * Битрейт записи, бит/с. `0` — как решит браузер (так было всегда).
 *
 * ⚠️ В СБОРКЕ НОЛЬ (решение владельца 02.10.2026). Запись на три минуты весит 3–4 МБ, для речи хватило бы
 * 32 кбит/с — файл в 4–6 раз меньше. Но слушается ли WKWebView этого параметра и не вырастут ли «тишина» и
 * обрывы, чтением кода не узнать: пауза в записи 11.09.2026 уже раз подняла обрывы на Safari с 3,6 до 18,3 %.
 * Число ставится с сервера (`limits.recordBitrate`) после замера на телефоне и так же снимается.
 */
export function recordBitrate(): number {
  const v = getLimit('recordBitrate', 0);
  return v >= 8000 && v <= 256000 ? Math.round(v) : 0;
}
